import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SolutionCondition {
  id: string;
  metric: string;
  type: string;
  high: string;
  medium: string;
  initial: string;
}

export interface ReadinessLevel {
  id: string;
  name: string;
  isCustom: boolean;
}

export interface SolutionConfig {
  name: string;
  readinessLevels: ReadinessLevel[];
  activeReadiness: string[]; // IDs of active readiness levels
  conditions: SolutionCondition[];
}

export interface ConfigurationState {
  purchek: SolutionConfig;
  cartManager: SolutionConfig;
  containment: SolutionConfig;
  subscription: SolutionConfig;
  pairedWheel: SolutionConfig;
  updatePurchekActiveReadiness: (readiness: string[]) => void;
  updateCartManagerActiveReadiness: (readiness: string[]) => void;
  updateContainmentActiveReadiness: (readiness: string[]) => void;
  updateSubscriptionActiveReadiness: (readiness: string[]) => void;
  updatePairedWheelActiveReadiness: (readiness: string[]) => void;
  addReadinessLevel: (name: string, solution: 'purchek' | 'cartManager' | 'containment' | 'subscription' | 'pairedWheel') => void;
  removeReadinessLevel: (id: string, solution: 'purchek' | 'cartManager' | 'containment' | 'subscription' | 'pairedWheel') => void;
  updatePurchekCondition: (conditionId: string, updates: Partial<SolutionCondition>) => void;
  updateCartManagerCondition: (conditionId: string, updates: Partial<SolutionCondition>) => void;
  updateContainmentCondition: (conditionId: string, updates: Partial<SolutionCondition>) => void;
  updateSubscriptionCondition: (conditionId: string, updates: Partial<SolutionCondition>) => void;
  updatePairedWheelCondition: (conditionId: string, updates: Partial<SolutionCondition>) => void;
  saveConfiguration: () => void;
}

const DEFAULT_READINESS_LEVELS: ReadinessLevel[] = [
  { id: 'high', name: 'High', isCustom: false },
  { id: 'medium', name: 'Medium', isCustom: false },
  { id: 'initial', name: 'Initial', isCustom: false }
];

const DEFAULT_PURCHEK_CONDITIONS: SolutionCondition[] = [
  {
    id: 'purchek_neighborhood_sites',
    metric: 'Number of Neighbourhood Purchek Sites',
    type: 'Condition',
    high: '5',
    medium: '3',
    initial: '1'
  },
  {
    id: 'purchek_penetration',
    metric: 'Purchek Penetration Percentage',
    type: 'Condition',
    high: '75',
    medium: '33',
    initial: '1'
  },
  {
    id: 'purchek_crime_incidents',
    metric: 'Number of Crime Incidents',
    type: 'Condition',
    high: '5000',
    medium: '2000',
    initial: '500'
  }
];

const DEFAULT_CARTMANAGER_CONDITIONS: SolutionCondition[] = [
  {
    id: 'cart_manager_estimated_carts',
    metric: 'Estimated Number of Carts',
    type: 'Condition',
    high: '300',
    medium: '150',
    initial: '50'
  },
  {
    id: 'cart_manager_hot_days',
    metric: 'Number of severely hot days',
    type: 'Condition',
    high: '90',
    medium: '60',
    initial: '30'
  },
  {
    id: 'cart_manager_cold_days',
    metric: 'Number of severely cold days',
    type: 'Condition',
    high: '90',
    medium: '60',
    initial: '30'
  }
];

const DEFAULT_CONTAINMENT_CONDITIONS: SolutionCondition[] = [
  {
    id: 'containment_neighborhood_sites',
    metric: 'Number of Neighbourhood Containment Sites',
    type: 'Condition',
    high: '5',
    medium: '3',
    initial: '1'
  },
  {
    id: 'containment_penetration',
    metric: 'Containment Penetration Percentage',
    type: 'Condition',
    high: '75',
    medium: '33',
    initial: '1'
  },
  {
    id: 'containment_crime_incidents',
    metric: 'Number of Crime Incidents',
    type: 'Condition',
    high: '5000',
    medium: '2000',
    initial: '500'
  },
  {
    id: 'containment_plaza_sites',
    metric: 'Part of a plaza',
    type: 'Condition',
    high: '1',
    medium: '1',
    initial: '1'
  },
  {
    id: 'containment_ordinance',
    metric: 'Ordinance',
    type: 'Condition',
    high: '1',
    medium: '1',
    initial: '1'
  }
];

const DEFAULT_SUBSCRIPTION_CONDITIONS: SolutionCondition[] = [
  {
    id: 'subscription_has_purchek',
    metric: 'Must have Purchek',
    type: 'Pre-condition',
    high: '1',
    medium: '1',
    initial: '1'
  }
];

const DEFAULT_PAIREDWHEEL_CONDITIONS: SolutionCondition[] = [
  {
    id: 'paired_wheel_has_not_installed',
    metric: 'Must not have Paired Wheels already',
    type: 'Pre-condition',
    high: '1',
    medium: '1',
    initial: '1'
  },
  {
    id: 'paired_wheel_penetration',
    metric: 'Paired wheel penetration',
    type: 'Condition',
    high: '75',
    medium: '33',
    initial: '1'
  },
  {
    id: 'paired_wheel_replacement_rate',
    metric: 'Single wheels replacement rate',
    type: 'Condition',
    high: '4',
    medium: '3',
    initial: '2'
  },
  {
    id: 'paired_wheel_service_frequency',
    metric: 'Service frequency',
    type: 'Condition',
    high: '50',
    medium: '25',
    initial: '0'
  }
];

const initializePurchek = (): SolutionConfig => ({
  name: 'Purchek',
  readinessLevels: DEFAULT_READINESS_LEVELS,
  activeReadiness: ['high'],
  conditions: DEFAULT_PURCHEK_CONDITIONS
});

const initializeCartManager = (): SolutionConfig => ({
  name: 'Cart Manager',
  readinessLevels: DEFAULT_READINESS_LEVELS,
  activeReadiness: ['high'],
  conditions: DEFAULT_CARTMANAGER_CONDITIONS
});

const initializeContainment = (): SolutionConfig => ({
  name: 'Cart Containment',
  readinessLevels: DEFAULT_READINESS_LEVELS,
  activeReadiness: ['high'],
  conditions: DEFAULT_CONTAINMENT_CONDITIONS
});

const initializeSubscription = (): SolutionConfig => ({
  name: 'Subscription',
  readinessLevels: DEFAULT_READINESS_LEVELS,
  activeReadiness: ['high'],
  conditions: DEFAULT_SUBSCRIPTION_CONDITIONS
});

const initializePairedWheel = (): SolutionConfig => ({
  name: 'Paired Wheel',
  readinessLevels: DEFAULT_READINESS_LEVELS,
  activeReadiness: ['high'],
  conditions: DEFAULT_PAIREDWHEEL_CONDITIONS
});

export const useConfigurationStore = create<ConfigurationState>()(
  persist(
    (set) => ({
      purchek: initializePurchek(),
      cartManager: initializeCartManager(),
      containment: initializeContainment(),
      subscription: initializeSubscription(),
      pairedWheel: initializePairedWheel(),
      updatePurchekActiveReadiness: (readiness: string[]) =>
        set((state) => ({
          purchek: {
            ...state.purchek,
            activeReadiness: readiness
          }
        })),
      updateCartManagerActiveReadiness: (readiness: string[]) =>
        set((state) => ({
          cartManager: {
            ...state.cartManager,
            activeReadiness: readiness
          }
        })),
      updateContainmentActiveReadiness: (readiness: string[]) =>
        set((state) => ({
          containment: {
            ...state.containment,
            activeReadiness: readiness
          }
        })),
      updateSubscriptionActiveReadiness: (readiness: string[]) =>
        set((state) => ({
          subscription: {
            ...state.subscription,
            activeReadiness: readiness
          }
        })),
      updatePairedWheelActiveReadiness: (readiness: string[]) =>
        set((state) => ({
          pairedWheel: {
            ...state.pairedWheel,
            activeReadiness: readiness
          }
        })),
      addReadinessLevel: (name: string, solution: 'purchek' | 'cartManager' | 'containment' | 'subscription' | 'pairedWheel') =>
        set((state) => {
          const newLevel: ReadinessLevel = {
            id: `custom_${Date.now()}`,
            name,
            isCustom: true
          };
          if (solution === 'purchek') {
            return {
              purchek: {
                ...state.purchek,
                readinessLevels: [...state.purchek.readinessLevels, newLevel]
              }
            };
          } else if (solution === 'cartManager') {
            return {
              cartManager: {
                ...state.cartManager,
                readinessLevels: [...state.cartManager.readinessLevels, newLevel]
              }
            };
          } else if (solution === 'containment') {
            return {
              containment: {
                ...state.containment,
                readinessLevels: [...state.containment.readinessLevels, newLevel]
              }
            };
          } else if (solution === 'subscription') {
            return {
              subscription: {
                ...state.subscription,
                readinessLevels: [...state.subscription.readinessLevels, newLevel]
              }
            };
          } else {
            return {
              pairedWheel: {
                ...state.pairedWheel,
                readinessLevels: [...state.pairedWheel.readinessLevels, newLevel]
              }
            };
          }
        }),
      removeReadinessLevel: (id: string, solution: 'purchek' | 'cartManager' | 'containment' | 'subscription' | 'pairedWheel') =>
        set((state) => {
          if (solution === 'purchek') {
            return {
              purchek: {
                ...state.purchek,
                readinessLevels: state.purchek.readinessLevels.filter((level) => level.id !== id),
                activeReadiness: state.purchek.activeReadiness.filter((rid) => rid !== id)
              }
            };
          } else if (solution === 'cartManager') {
            return {
              cartManager: {
                ...state.cartManager,
                readinessLevels: state.cartManager.readinessLevels.filter((level) => level.id !== id),
                activeReadiness: state.cartManager.activeReadiness.filter((rid) => rid !== id)
              }
            };
          } else if (solution === 'containment') {
            return {
              containment: {
                ...state.containment,
                readinessLevels: state.containment.readinessLevels.filter((level) => level.id !== id),
                activeReadiness: state.containment.activeReadiness.filter((rid) => rid !== id)
              }
            };
          } else if (solution === 'subscription') {
            return {
              subscription: {
                ...state.subscription,
                readinessLevels: state.subscription.readinessLevels.filter((level) => level.id !== id),
                activeReadiness: state.subscription.activeReadiness.filter((rid) => rid !== id)
              }
            };
          } else {
            return {
              pairedWheel: {
                ...state.pairedWheel,
                readinessLevels: state.pairedWheel.readinessLevels.filter((level) => level.id !== id),
                activeReadiness: state.pairedWheel.activeReadiness.filter((rid) => rid !== id)
              }
            };
          }
        }),
      updatePurchekCondition: (conditionId: string, updates: Partial<SolutionCondition>) =>
        set((state) => ({
          purchek: {
            ...state.purchek,
            conditions: state.purchek.conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, ...updates } : condition
            )
          }
        })),
      updateCartManagerCondition: (conditionId: string, updates: Partial<SolutionCondition>) =>
        set((state) => ({
          cartManager: {
            ...state.cartManager,
            conditions: state.cartManager.conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, ...updates } : condition
            )
          }
        })),
      updateContainmentCondition: (conditionId: string, updates: Partial<SolutionCondition>) =>
        set((state) => ({
          containment: {
            ...state.containment,
            conditions: state.containment.conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, ...updates } : condition
            )
          }
        })),
      updateSubscriptionCondition: (conditionId: string, updates: Partial<SolutionCondition>) =>
        set((state) => ({
          subscription: {
            ...state.subscription,
            conditions: state.subscription.conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, ...updates } : condition
            )
          }
        })),
      updatePairedWheelCondition: (conditionId: string, updates: Partial<SolutionCondition>) =>
        set((state) => ({
          pairedWheel: {
            ...state.pairedWheel,
            conditions: state.pairedWheel.conditions.map((condition) =>
              condition.id === conditionId ? { ...condition, ...updates } : condition
            )
          }
        })),
      saveConfiguration: () =>
        set((state) => {
          const purchekActiveLevel = state.purchek.activeReadiness[0];
          const cartManagerActiveLevel = state.cartManager.activeReadiness[0];
          const containmentActiveLevel = state.containment.activeReadiness[0];
          const subscriptionActiveLevel = state.subscription.activeReadiness[0];
          const pairedWheelActiveLevel = state.pairedWheel.activeReadiness[0];

          // Build Purchek readiness levels with their conditions
          const purchekReadinessLevelsData = state.purchek.readinessLevels.reduce((acc, level) => {
            acc[level.id] = {
              name: level.name,
              isCustom: level.isCustom,
              isActive: level.id === purchekActiveLevel,
              conditions: state.purchek.conditions.reduce((condAcc, condition) => {
                condAcc[condition.metric] = (condition as any)[level.id] || '';
                return condAcc;
              }, {} as Record<string, string>)
            };
            return acc;
          }, {} as Record<string, any>);

          // Build Cart Manager readiness levels with their conditions
          const cartManagerReadinessLevelsData = state.cartManager.readinessLevels.reduce((acc, level) => {
            acc[level.id] = {
              name: level.name,
              isCustom: level.isCustom,
              isActive: level.id === cartManagerActiveLevel,
              conditions: state.cartManager.conditions.reduce((condAcc, condition) => {
                condAcc[condition.metric] = (condition as any)[level.id] || '';
                return condAcc;
              }, {} as Record<string, string>)
            };
            return acc;
          }, {} as Record<string, any>);

          // Build Containment readiness levels with their conditions
          const containmentReadinessLevelsData = state.containment.readinessLevels.reduce((acc, level) => {
            acc[level.id] = {
              name: level.name,
              isCustom: level.isCustom,
              isActive: level.id === containmentActiveLevel,
              conditions: state.containment.conditions.reduce((condAcc, condition) => {
                condAcc[condition.metric] = (condition as any)[level.id] || '';
                return condAcc;
              }, {} as Record<string, string>)
            };
            return acc;
          }, {} as Record<string, any>);

          // Build Subscription readiness levels with their conditions
          const subscriptionReadinessLevelsData = state.subscription.readinessLevels.reduce((acc, level) => {
            acc[level.id] = {
              name: level.name,
              isCustom: level.isCustom,
              isActive: level.id === subscriptionActiveLevel,
              conditions: state.subscription.conditions.reduce((condAcc, condition) => {
                condAcc[condition.metric] = (condition as any)[level.id] || '';
                return condAcc;
              }, {} as Record<string, string>)
            };
            return acc;
          }, {} as Record<string, any>);

          // Build Paired Wheel readiness levels with their conditions
          const pairedWheelReadinessLevelsData = state.pairedWheel.readinessLevels.reduce((acc, level) => {
            acc[level.id] = {
              name: level.name,
              isCustom: level.isCustom,
              isActive: level.id === pairedWheelActiveLevel,
              conditions: state.pairedWheel.conditions.reduce((condAcc, condition) => {
                condAcc[condition.metric] = (condition as any)[level.id] || '';
                return condAcc;
              }, {} as Record<string, string>)
            };
            return acc;
          }, {} as Record<string, any>);

          const configData = {
            purchek: {
              solutionName: state.purchek.name,
              activeReadinessLevel: purchekActiveLevel,
              readinessLevels: purchekReadinessLevelsData
            },
            cartManager: {
              solutionName: state.cartManager.name,
              activeReadinessLevel: cartManagerActiveLevel,
              readinessLevels: cartManagerReadinessLevelsData
            },
            containment: {
              solutionName: state.containment.name,
              activeReadinessLevel: containmentActiveLevel,
              readinessLevels: containmentReadinessLevelsData
            },
            subscription: {
              solutionName: state.subscription.name,
              activeReadinessLevel: subscriptionActiveLevel,
              readinessLevels: subscriptionReadinessLevelsData
            },
            pairedWheel: {
              solutionName: state.pairedWheel.name,
              activeReadinessLevel: pairedWheelActiveLevel,
              readinessLevels: pairedWheelReadinessLevelsData
            }
          };
          console.log('Configuration Saved:', configData);
          return state;
        })
    }),
    {
      name: 'configuration-store'
    }
  )
);

// Clear old persisted state to force fresh initialization
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('configuration-store');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Clear if missing readinessLevels or if Ordinance condition has old format
      const needsClear = parsed.state && parsed.state.purchek && !parsed.state.purchek.readinessLevels;
      const ordinanceCondition = parsed.state?.containment?.conditions?.find((c: any) => c.metric === 'Ordinance');
      const ordinanceNeedsUpdate = ordinanceCondition && (ordinanceCondition.medium === '0' || ordinanceCondition.initial === '0');

      if (needsClear || ordinanceNeedsUpdate) {
        localStorage.removeItem('configuration-store');
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }
}
